import { useState } from "react"
import styled from "styled-components"

import {
  ClockIcon,
  HomeIcon,
  ProfileIcon,
  TaskManagementIcon,
} from "@minuthq/meatball-ui-icons"
import { spacing, TSelectOption } from "@minuthq/meatball-ui-react"
import { setHours, startOfHour } from "date-fns"

import { AssigneeDropdownSelect } from "src/components/TaskManagement/AssigneeDropdownSelect"
import { TaskMotion } from "src/components/TaskManagement/TaskDetails/TaskMotion"
import { THome } from "src/data/homes/types/homeTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHomes } from "src/data/organizations/queries/homeQueries"
import { useTaskTypeLabels } from "src/data/taskManagement/hooks/useFetchTaskTableData"
import { isUnassignedOptionId } from "src/data/taskManagement/taskManagementFilters"
import { TTask, TTaskType } from "src/data/taskManagement/taskManagementTypes"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { Divider } from "src/ui/Divider/Divider"
import { DropdownSelect } from "src/ui/DropdownSelect/DropdownSelect"
import { MDateTimePickerText } from "src/ui/MDateTimePicker/MDateTimePickerText"
import { MTooltip } from "src/ui/MTooltip/MTooltip"

export function TaskMetadata({
  task,
  onUpdateTask,
  home,
}: {
  task?: TTask
  onUpdateTask: (updates: Partial<TTask>) => void
  home?: THome
}) {
  const { t, langKeys } = useTranslate()
  const user = useGetUser()

  const taskTypes = useTaskTypeLabels()

  if (!task || !home) {
    return null
  }

  const taskCategory: TSelectOption<TTaskType>[] = Object.entries(
    taskTypes
  ).map(([type, label]) => ({
    label,
    value: type as TTaskType,
  }))

  return (
    <Box>
      <StyledContainer>
        <MTooltip title={t(langKeys.task_management_task_type)}>
          <TaskManagementIcon width={22} />
        </MTooltip>
        <DropdownSelect
          label={taskCategory.find((t) => t.value === task.category)?.label}
          options={taskCategory}
          selectedValue={task.category}
          onChange={(value) => {
            onUpdateTask({ category: value.option.value })
          }}
          mButtonVariant="minimal"
        />

        <MTooltip title={t(langKeys.home)}>
          <HomeIcon width={22} />
        </MTooltip>
        <HomesDropdownSelect
          task={task}
          home={home}
          onChange={(option) => {
            onUpdateTask({ home_id: option.value })
          }}
        />

        <MTooltip title={t(langKeys.task_management_task_assign_to)}>
          <ProfileIcon width={22} />
        </MTooltip>
        <AssigneeDropdownSelect
          initialValue={task.assigned_to.assignee_id || ""}
          homeId={home.home_id}
          onChange={(value) => {
            isUnassignedOptionId(value)
              ? onUpdateTask({ assigned_to: { type: "unassigned" } })
              : onUpdateTask({
                  assigned_to: {
                    type: "member",
                    assignee_id: value,
                  },
                })
          }}
        />

        <MTooltip title={t(langKeys.task_management_task_due_date)}>
          <ClockIcon width={22} />
        </MTooltip>
        <MDateTimePickerText
          timePickerInputProps={{
            clockType: user.clock_type,
          }}
          initialDate={
            task.time_window.due_date
              ? new Date(task.time_window.due_date)
              : startOfHour(setHours(new Date(), 12))
          }
          key={task.time_window.due_date}
          onChange={(date) =>
            date &&
            onUpdateTask({ time_window: { due_date: date.toISOString() } })
          }
          timePrefixText={t(langKeys.at_prefix_text)}
        />
      </StyledContainer>

      <Divider />
      <TaskMotion task={task} timezone={home.timezone} />
    </Box>
  )
}

function HomesDropdownSelect({
  task,
  home,
  onChange,
}: {
  task: TTask
  home: THome
  onChange: (option: TSelectOption<string>) => void
}) {
  const { orgId } = useOrganization()

  const [search, setSearch] = useState("")

  const fetchHomes = useFetchHomes({
    orgId,
    filters: {
      name: search,
    },
  })

  const homes = fetchHomes.data?.homes || []

  const homeOptions: TSelectOption<string>[] =
    homes.map((home) => ({
      label: home.name || "",
      value: home.home_id,
    })) || []

  return (
    <DropdownSelect
      label={
        home.name ??
        homeOptions.find((option) => option.value === task.home_id)?.label ??
        ""
      }
      options={homeOptions}
      selectedValue={task.home_id ?? null}
      onChange={(value) => onChange(value.option)}
      onSearch={setSearch}
      mButtonVariant="minimal"
      debounceDelay={400}
    />
  )
}

const Box = styled.div`
  display: grid;
  gap: ${spacing.M};
`

const StyledContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${spacing.XS};
  align-items: center;
`
